'use client';

import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { useCallback, useMemo, useState } from 'react';
import { ConfirmDialog, GenericDetailPage, InfoDialog, Tabs } from '@repo/components';
import GroupInfoBox from 'src/components/GroupInfoBox';
import { PERMISSIONS } from '@repo/shared/domain';
import { AddOwnerForm, BlockGroupForm, GroupForm, UnblockGroupForm } from '@repo/forms';
import { EventEmitter } from '@repo/shared/helpers';
import { getVerifyPermission } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getGroup, getGroupRoles, getExtraDelegatedRoles } from 'src/api/admin/groups';
import { getGroupTasks } from 'src/api/admin/tasks';
import { getGroupApprovals } from 'src/api/admin/approvals';
import { getGroupTypes, getRequirements } from 'src/api/admin/types';
import { getGroupDocuments } from 'src/api/admin/documents';
export const Route = createFileRoute('/_admin/groups/$groupId')({
	loader: async ({ params }) => {
		const { groupId } = params;
		const [group, types, tasks, approvals, groupRoles, extraDelegatedRoles, groupDocuments, requirements] =
			await Promise.all([
				getGroup(groupId),
				getGroupTypes(),
				getGroupTasks(groupId),
				getGroupApprovals(groupId),
				getGroupRoles(groupId),
				getExtraDelegatedRoles(groupId),
				getGroupDocuments(groupId),
				getRequirements(),
			]);

		return {
			group,
			types: types.data,
			tasks: tasks.data,
			approvals: approvals.data,
			groupRoles,
			extraDelegatedRoles,
			requirements,
		};
	},
	component: GroupDetailLayout,
});

function GroupDetailLayout() {
	const { groupId } = Route.useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();
	const {
		group: initialGroup,
		types: initialTypes,
		tasks: initialTasks,
		approvals: initialApprovals,
		groupRoles: initialGroupRoles,
		extraDelegatedRoles: initialExtraDelegatedRoles,
	} = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();
	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const usesCompanyApiChecks = featureFlags?.USES_COMPANY_API_CHECKS || false;

	const [isFormOpened, setIsFormOpened] = useState(false);
	const [isGroupArchiveDialogOpen, setIsGroupArchiveDialogOpen] = useState(false);
	const [ownerToRemoveId, setOwnerToRemoveId] = useState<string | null>(null);
	const [isGroupBlockDialogOpen, setIsGroupBlockDialogOpen] = useState(false);
	const [isGroupUnblockDialogOpen, setIsGroupUnblockDialogOpen] = useState(false);
	const [isAddOwnerDialogOpen, setIsAddOwnerDialogOpen] = useState(false);
	const [isNotPossibleDialogOpen, setNotPossibleDialogOpen] = useState(false);

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const { data: group, refetch: refetchGroup } = useQuery({
		queryKey: ['group', groupId],
		queryFn: () => getGroup(groupId),
		initialData: initialGroup,
	});

	const { data: groupRoles } = useQuery({
		queryKey: ['group-roles', groupId],
		queryFn: () => getGroupRoles(groupId),
		initialData: initialGroupRoles,
	});

	const { data: extraDelegatedRoles } = useQuery({
		queryKey: ['group-extra-delegated-roles', groupId],
		queryFn: () => getExtraDelegatedRoles(groupId),
		initialData: initialExtraDelegatedRoles,
	});

	const { data: tasks } = useQuery({
		queryKey: ['group-tasks-overview', groupId],
		queryFn: () => getGroupTasks(groupId),
		initialData: { data: initialTasks },
	});

	const { data: approvals } = useQuery({
		queryKey: ['group-approvals-overview', groupId],
		queryFn: () => getGroupApprovals(groupId),
		initialData: { data: initialApprovals },
	});

	const { data: groupDocuments } = useQuery({
		queryKey: ['group-documents-overview', groupId],
		queryFn: () => getGroupDocuments(groupId),
		initialData: { data: [] },
	});

	const hasDocsForReview = groupDocuments?.data?.some((doc) => doc.status === 'inReview') || false;
	const hasTasksTodo = (tasks?.data || []).some((t) => t.status === 'todo');
	const hasApprovalsTodo = (approvals?.data || []).some((t) => t.status === 'todo');

	useBreadcrumbLevel(0, 'Companies', '/groups');
	useBreadcrumbLevel(1, group?.name || '');

	const revalidateGroupDetail = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['group', groupId] });
		queryClient.invalidateQueries({ queryKey: ['group-roles', groupId] });
		queryClient.invalidateQueries({ queryKey: ['group-documents-overview', groupId] });
		queryClient.invalidateQueries({ queryKey: ['group-tasks-overview', groupId] });
		queryClient.invalidateQueries({ queryKey: ['group-approvals-overview', groupId] });
	}, [queryClient, groupId]);

	const revalidateGroups = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['groups'] });
	}, [queryClient]);

	const defaultGroupData = {
		...group,
	};

	const handleEditGroup = async (formData) => {
		let isError: null | boolean = null;
		if (formData) {
			await request
				.put(`/api/groups/${group?.id}`, formData)
				.then(() => {
					EventEmitter.emit('toast', {
						type: 'success',
						message: 'Company saved',
					});
				})
				.catch(() => {
					isError = true;
					EventEmitter.emit('toast', {
						type: 'error',
						message: 'Failed to save Company. Did you enter all data correctly?',
					});
				});
		}

		revalidateGroupDetail();
		revalidateGroups();
		if (!isError) {
			setIsFormOpened(false);
		}
	};

	const toggleReview = async () => {
		await request
			.put(`/api/groups/${group?.id}/set-review-status`, {
				markForReview: !group?.markedForReview,
			})
			.then(() => {
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Company saved',
				});
			})
			.catch(() => {
				EventEmitter.emit('toast', {
					type: 'error',
					message: 'Failed to update review status.',
				});
			});

		revalidateGroupDetail();
		revalidateGroups();
	};

	const restoreGroup = async () => {
		await request
			.put(`/api/groups/${group?.id}/restore`, {})
			.then(() => {
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Company restored',
				});
			})
			.catch(() => {
				EventEmitter.emit('toast', {
					type: 'error',
					message: 'Failed to restore company.',
				});
			});
		revalidateGroupDetail();
		revalidateGroups();
	};

	const archiveGroup = async () => {
		try {
			await request.del(`/api/groups/${group?.id}`, {});
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to archive company.',
			});
			return;
		}
		EventEmitter.emit('toast', {
			type: 'success',
			message: 'Company archived',
		});
		setIsGroupArchiveDialogOpen(false);
		revalidateGroupDetail();
		revalidateGroups();
	};

	// Get current tab from pathname
	const pathSegments = location.pathname.split('/');
	const selectedLayoutSegment = pathSegments[pathSegments.length - 1] as
		| 'details'
		| 'contact-details'
		| 'users'
		| 'documents'
		| 'tasks'
		| 'approvals'
		| 'events'
		| 'logs';

	const { tabs, tabIndexPerTabId } = useMemo(() => {
		const builtTabs = [
			...(group?.type?.Form
				? [
						{
							id: 'details',
							label: 'Details',
						},
					]
				: []),
			{
				id: 'contact-details',
				label: 'Contact Details',
			},
			...(verifyPermission([PERMISSIONS.adminUsersView])
				? [
						{
							id: 'users',
							label: 'Users',
						},
					]
				: []),
			{
				id: 'documents',
				label: 'Documents',
				error: hasDocsForReview,
			},
			{
				id: 'tasks',
				label: 'Tasks',
				error: hasTasksTodo,
			},
			{
				id: 'approvals',
				label: 'Approvals',
				error: hasApprovalsTodo,
			},
			...(verifyPermission([PERMISSIONS.adminEventsView])
				? [
						{
							id: 'events',
							label: 'Events',
						},
					]
				: []),
			...(verifyPermission([PERMISSIONS.adminGrouplogsView])
				? [
						{
							id: 'logs',
							label: 'Logs',
						},
					]
				: []),
		];
		const tabIndexPerTabId = builtTabs.reduce(
			(acc, cur, index) => {
				return {
					...acc,
					[cur.id]: index,
				};
			},
			{} as Record<string, number>,
		);

		return {
			tabs: builtTabs,
			tabIndexPerTabId,
		};
	}, [group?.type?.Form, hasDocsForReview, hasTasksTodo, hasApprovalsTodo, verifyPermission]);

	const handleTabChange = (newValue: number) => {
		const tabId = tabs[newValue].id;
		navigate({ to: `/groups/$groupId/${tabId}`, params: { groupId } });
	};

	const handleOpenForm = useCallback(() => {
		setIsFormOpened(true);
	}, []);

	const handleCloseForm = () => {
		setIsFormOpened(false);
	};

	const handleCloseGroupArchiveDialog = () => {
		setIsGroupArchiveDialogOpen(false);
	};

	const archiveGroupPrompt = () => setIsGroupArchiveDialogOpen(true);

	const handleOpenGroupBlockDialog = () => {
		setIsGroupBlockDialogOpen(true);
	};

	const handleCloseBlockGroupDialog = () => {
		setIsGroupBlockDialogOpen(false);
	};

	const handleOpenGroupUnblockDialog = () => {
		setIsGroupUnblockDialogOpen(true);
	};

	const handleCloseUnblockGroupDialog = () => {
		setIsGroupUnblockDialogOpen(false);
	};

	const handleOpenAddOwnerDialog = () => {
		setIsAddOwnerDialogOpen(true);
	};

	const handleCloseAddOwnerDialog = () => {
		setIsAddOwnerDialogOpen(false);
	};

	const blockGroup = async (data) => {
		try {
			await request.post(`/api/groups/${group?.id}/block`, data);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Successfully blocked company.',
			});
			setIsGroupBlockDialogOpen(false);
			revalidateGroupDetail();
			revalidateGroups();
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to block company.',
			});
		}
	};

	const unblockGroup = async (data) => {
		try {
			await request.post(`/api/groups/${group?.id}/unblock`, data);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Successfully unblocked company.',
			});
			setIsGroupUnblockDialogOpen(false);
			revalidateGroupDetail();
			revalidateGroups();
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to unblock company.',
			});
		}
	};

	const addOwners = async (formData) => {
		try {
			await request.post(`/api/groups/${group?.id}/add-owners`, formData);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Successfully added owner(s).',
			});
			setIsAddOwnerDialogOpen(false);
			revalidateGroupDetail();
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to add company admin(s).',
			});
		}
	};

	const handleOwnerRemoveClick = (ownerId: string) => {
		const adminIds = group?.adminIds || [];

		if (adminIds.includes(ownerId) && group?.type?.mandatoryGroupOwner && adminIds.length <= 1) {
			setNotPossibleDialogOpen(true);
		} else {
			setOwnerToRemoveId(ownerId);
		}
	};

	const removeOwner = async (userId: string) => {
		try {
			await request.del(`/api/groups/${group?.id}/owners/${userId}`);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Successfully removed owner.',
			});
			revalidateGroupDetail();
			setOwnerToRemoveId(null);
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to remove owner.',
			});
		}
	};

	if (!group) {
		return null;
	}

	return (
		<>
			<GenericDetailPage>
				<GenericDetailPage.Content>
					<GenericDetailPage.Content.Notifications>
						{group.archived && (
							<Alert
								severity="info"
								variant="filled"
								action={
									verifyPermission([PERMISSIONS.adminGroupsArchive]) && (
										<Button color="inherit" onClick={restoreGroup}>
											Restore
										</Button>
									)
								}
							>
								This company is archived.
							</Alert>
						)}
						{group.isBlocked && (
							<Alert severity="error" variant="filled">
								This company is blocked.
							</Alert>
						)}
						{group.markedForReview?.length && (
							<Alert severity="error" variant="filled" sx={{ '& ul': { margin: 0, paddingLeft: 2 } }}>
								This company is currently locked and requires to be reviewed.
								<ul>
									{group.markedForReview?.includes('documents') && <li>Documents for review</li>}
									{group.markedForReview?.includes('notUnique') && <li>VAT number is not unique</li>}
									{group.markedForReview?.includes('vatMissing') && <li>VAT number is missing</li>}
									{group.markedForReview?.includes('vatNotChecked') && <li>VAT number could not be checked</li>}
									{group.markedForReview?.includes('vatExpired') && <li>VAT number is expired</li>}
									{group.markedForReview?.includes('vcaMissing') && (
										<li>No valid VCA documentation found on the company</li>
									)}
									{group.markedForReview?.includes('manual') && <li>Manually placed in review</li>}
								{usesCompanyApiChecks && group.markedForReview?.includes('inhoudingsplicht') && (
									<li>Inhoudingsplicht applies on group</li>
								)}
								</ul>
							</Alert>
						)}

						{hasTasksTodo && (
							<Alert severity="warning" variant="filled" sx={{ '& ul': { margin: 0, paddingLeft: 2 } }}>
								This company has open tasks.
							</Alert>
						)}
						{hasApprovalsTodo && (
							<Alert
								severity="warning"
								variant="filled"
								sx={{
									marginBottom: 2,
									'& ul': { margin: 0, paddingLeft: 2 },
								}}
							>
								This company has one or more approval tasks pending approval.
							</Alert>
						)}
					</GenericDetailPage.Content.Notifications>
					<GroupInfoBox
						group={group}
						groupRoles={groupRoles || []}
						openEditForm={handleOpenForm}
						openArchiveDialog={archiveGroupPrompt}
						openAddOwnerDialog={handleOpenAddOwnerDialog}
						openOwnerRemoveDialog={handleOwnerRemoveClick}
						openReviewDialog={toggleReview}
						openUnblockDialog={handleOpenGroupUnblockDialog}
						openBlockDialog={handleOpenGroupBlockDialog}
						permissions={{
							canAddOwner: verifyPermission([PERMISSIONS.adminGroupsCreate]),
							canEdit: verifyPermission([PERMISSIONS.adminGroupsEdit]),
							canArchive: verifyPermission([PERMISSIONS.adminGroupsArchive]),
							canReview: verifyPermission([PERMISSIONS.adminGroupsReview]),
							canBlock: verifyPermission([PERMISSIONS.adminSecurityBlockCompanies]),
						}}
						usesCompanyApiChecks={usesCompanyApiChecks}
						canBeReviewed={group.status !== 'inReview' || !hasDocsForReview}
					/>
				</GenericDetailPage.Content>
				<Tabs value={tabIndexPerTabId[selectedLayoutSegment] ?? 0} onChange={handleTabChange} className="px-4">
					<Tabs.TabList>
						{tabs.map((tab, index) => (
							<Tabs.Tab
								key={tab.id}
								value={index}
								label={
									<div className="flex items-center gap-2">
										{tab.label}
										{tab.error && <span className="inline-flex h-2 w-2 rounded-full bg-red-600" />}
									</div>
								}
							/>
						))}
					</Tabs.TabList>
				</Tabs>
				<Outlet />
			</GenericDetailPage>
			<ConfirmDialog
				isOpen={isGroupArchiveDialogOpen}
				cancelAction={handleCloseGroupArchiveDialog}
				confirmAction={archiveGroup}
				cancelLabel="Cancel"
				confirmLabel="Archive"
			>
				Are you sure you want to archive this company?
			</ConfirmDialog>

			<ConfirmDialog
				isOpen={!!ownerToRemoveId}
				cancelAction={() => setOwnerToRemoveId(null)}
				confirmAction={() => ownerToRemoveId && removeOwner(ownerToRemoveId)}
				cancelLabel="Cancel"
				confirmLabel="Remove"
			>
				Are you sure you want to remove this company admin?
			</ConfirmDialog>

			<InfoDialog isOpen={isNotPossibleDialogOpen} onClose={() => setNotPossibleDialogOpen(false)}>
				The company needs at least one company admin. Add a new company admin before removing the current one.
			</InfoDialog>

			<GroupForm
				isOpen={isFormOpened}
				data={defaultGroupData}
				onClose={handleCloseForm}
				onSubmit={handleEditGroup}
				types={initialTypes}
				roles={extraDelegatedRoles || []}
			/>
			<BlockGroupForm isOpen={isGroupBlockDialogOpen} onClose={handleCloseBlockGroupDialog} onSubmit={blockGroup} />
			<UnblockGroupForm
				isOpen={isGroupUnblockDialogOpen}
				onClose={handleCloseUnblockGroupDialog}
				onSubmit={unblockGroup}
			/>
			<AddOwnerForm
				isOpen={isAddOwnerDialogOpen}
				onClose={handleCloseAddOwnerDialog}
				onSubmit={addOwners}
				source="company"
			/>
		</>
	);
}
